@import "tailwindcss";

/* ── Type system — DM Serif Display (Tiempos-alternative) + Inter ──
   Masters edition: heritage editorial serif for display, Inter for
   body. DM Serif Display ships in regular weight only — that's fine,
   it's a display face used at large sizes for the hero headline. */
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@300;400;500;600;700&display=swap');

/* ── Custom Properties — Masters edition ──
   Augusta deep green replaces the prior sage. Paper cream stays the
   page surface. Greens-on-cream with one yellow accent reserved for
   success states. Square corners (we don't redefine here; component
   classes are swept separately). */
:root {
  /* ── Color: surfaces ──
     Page paper tinted to a warm clubhouse cream (#F8F4EC, matching
     the meta theme-color we already declare in index.html). Per the
     2026-05-06 critic audit (E2): "tint the canvas — the wash will
     materialize, the green disc will calm, the hairlines will sing.
     One token, whole atmosphere." Cards stay pure white so they
     elevate slightly off the cream paper, giving subtle Augusta
     program-note depth without explicit shadows. */
  --surface-0:     #F8F4EC;  /* page — warm clubhouse cream */
  --surface-1:     #FDFBF6;  /* cards — one warmth-tier off the page so
                                they elevate softly without contrast
                                shock. Round-9 critic E2: "cards still
                                pure white on cream — atmosphere ceiling.
                                A 4-degree warm shift keeps elevation
                                legible while letting the cards belong
                                to the same paper family." */
  --surface-2:     #FAF7F0;  /* recessed (very light cream) */
  --surface-3:     #F2EBDB;  /* deeper accent surface */
  --surface-4:     #E0D5BD;  /* warm hairline */

  /* ── Color: ink (5-tier text — green-charcoal, never pure black) ── */
  --ink-1:         #003523;  /* headlines — Augusta near-black green */
  --ink-2:         #4F564E;  /* labels, secondary */
  --ink-3:         #8C8A82;  /* hints, microcopy */
  --ink-4:         #B6B0A4;  /* disabled, ghost text */
  --ink-5:         #E5DDD3;  /* dividers in copy */

  /* ── Color: accent (Augusta deep green) ── */
  --accent:         #006747;  /* primary — Masters' deep green */
  --accent-hover:   #003523;  /* near-black green */
  --accent-deep:    #003523;  /* heavy weight on cream */
  --accent-muted:   rgba(0, 103, 71, 0.14);
  --accent-subtle:  rgba(0, 103, 71, 0.07);
  --accent-glow:    rgba(0, 103, 71, 0.20);
  --text-on-accent: #FFFFFF;

  /* ── Recording (still green — dignified, not alarming) ── */
  --recording:       #006747;
  --recording-hover: #003523;
  --recording-glow:  rgba(0, 103, 71, 0.18);
  --recording-muted: rgba(0, 103, 71, 0.07);

  --soothe-glow:     rgba(0, 103, 71, 0.12);

  /* ── Semantic ── */
  --success:        #107D57;  /* Masters mid-green */
  --warning:        #B47A0A;
  --error:          #B23A2C;
  --info:           #3D6E8C;

  /* ── Yellow accent — used SURGICALLY (success badges only) ── */
  --gold:           #FCE300;  /* the Masters yellow */
  --gold-soft:      rgba(252, 227, 0, 0.18);

  /* ── Borders — round-7 critic E2 fix: --border-default flipped
     from 20% to 10% so the routine hairlines on cream paper read as
     program-note restraint. --border-strong (20%) added for hover,
     focus, or moments of explicit emphasis. The cream-tinted ground
     (--surface-0 = #F8F4EC) makes 10% read as a confident hairline
     where on pure white it nearly disappeared. */
  --border-subtle:  rgba(0, 53, 35, 0.06);
  --border-default: rgba(0, 53, 35, 0.10);
  --border-strong:  rgba(0, 53, 35, 0.20);
  --border-accent:  rgba(0, 103, 71, 0.36);
  --border-hairline: var(--surface-4);

  /* ── Eyebrow type tokens (round-7 critic E1 + E2 unification) ──
     Real CSS custom properties — every uppercase eyebrow on a brand
     surface should consume these. Per E1: "build a real eyebrow type
     spec — eyebrow-xs (10px), eyebrow-sm (11px), one family, one
     weight, one tracking — then audit every uppercase span against
     it and delete the orphans." Per E2: "until --eyebrow-size,
     --eyebrow-weight, --eyebrow-tracking actually exist as custom
     properties and every eyebrow consumes them, this looks tidy in
     a ZIP audit and inconsistent on screen." */
  --eyebrow-size:           11px;
  --eyebrow-size-sm:        10px;
  --eyebrow-weight:         600;
  --eyebrow-tracking:       0.22em;
  --eyebrow-tracking-tight: 0.18em;

  /* ── Serif scale (round-11 critic E1, fourth time of asking) ──
     Modular 1.250 ratio off a 16px base, mapped to fluid clamps so
     the scale holds across the viewport range we ship. Per E1: "until
     a --serif-h1 / --serif-h2 / --serif-h3 / --serif-body exists in
     index.css and every serif fontSize consumes one, the headline
     scale is vibes-based, not systemic." Components retire their
     ad-hoc clamp(N, vw, M) calls in favor of these tokens. Mapping
     guide:
       --serif-display = mobile hero command (largest)
       --serif-h1      = desktop hero command, modal venue name
       --serif-h2      = right-column manifesto, Status detail title,
                         post-submit headline
       --serif-h3      = right-column venue confirmation
       --serif-h4      = right-column service h2
       --serif-h5      = post-submit secondary serif
       --serif-body    = right-column + status body */
  --serif-display: clamp(48px, 9vw, 76px);   /* mobile hero command */
  --serif-h1:      clamp(40px, 5.2vw, 61px); /* desktop hero, modal venue */
  --serif-h2:      clamp(31px, 3.4vw, 49px); /* right-column manifesto */
  --serif-h3:      clamp(20px, 1.8vw, 31px); /* right-column venue confirmation, modal h2 */
  --serif-h4:      clamp(17px, 1.32vw, 21px);/* right-column service h2 */
  --serif-h5:      16px;                     /* secondary serif */
  --serif-body:    clamp(14px, 0.94vw, 16px);/* right-column body, status body */
}

/* Round-10 utility classes — let components opt in to the eyebrow recipe
   without inlining font-size/weight/tracking. Per critic E1 + E2 round-5:
   "tokens that exist only in the stylesheet quietly corrode trust in the
   ones that do — every uppercase eyebrow on every brand surface should
   consume the tokens." */
.eyebrow-token {
  font-size: var(--eyebrow-size);
  font-weight: var(--eyebrow-weight);
  letter-spacing: var(--eyebrow-tracking);
}
.eyebrow-token-sm {
  font-size: var(--eyebrow-size-sm);
  font-weight: var(--eyebrow-weight);
  letter-spacing: var(--eyebrow-tracking);
}

:root {
  /* (continuing the original :root block — Tailwind v4 doesn't mind a split) */

  /* ── Glass ── */
  --glass-bg:       rgba(255, 255, 255, 0.86);
  --glass-border:   rgba(0, 53, 35, 0.10);

  /* ── Conversation bubbles (kept for backward compat; Receipt component
     no longer uses these — but uses --bubble-user-bg below). ── */
  --bubble-user:       var(--accent);
  --bubble-user-text:  #FFFFFF;
  --bubble-agent:      var(--surface-1);
  --bubble-agent-text: var(--ink-1);

  /* User message rows in Receipt — outline only, no fill. The hairline
     does the role-cue work without competing with the conversation. */
  --bubble-user-outline: rgba(0, 0, 0, 0.10);

  /* Soft fill for captured-token highlights in the PreviewCard legend.
     Per Ryan: the bold dark-green flip was too distracting — captured
     tokens should still be visibly "got" but recede enough that the
     remaining grey tokens still feel prevalent. ~55% of the deep
     accent so it reads as "soft moss" against the black-ink background. */
  --accent-soft: rgba(0, 103, 71, 0.62);

  /* ── Legacy aliases (do not remove — old components still reference) ── */
  --bg-primary:     var(--surface-0);
  --bg-surface-1:   var(--surface-1);
  --bg-surface-2:   var(--surface-2);
  --bg-surface-3:   var(--surface-3);
  --bg-hover:       var(--surface-3);
  --bg-secondary:   var(--surface-1);
  --bg-card:        var(--surface-1);
  --bg-elevated:    var(--surface-1);
  --text-primary:   var(--ink-1);
  --text-secondary: var(--ink-2);
  --text-muted:     var(--ink-3);
  --text-ghost:     var(--ink-4);

  /* ── Type families — Masters edition ──
     Display serif: DM Serif Display (Tiempos Headline alternative).
     Body sans: Inter (Benton Sans alternative). Mono unchanged. */
  --font-sans:  'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  --font-serif: 'DM Serif Display', 'Tiempos Headline', 'Source Serif 4', 'Iowan Old Style', Georgia, serif;
  --font-mono:  ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace;

  /* ── Type scale (mobile values; desktop overrides at md: breakpoint) ── */
  --text-display: 3rem;       /* 48px — hero on mobile */
  --text-h1:      2rem;       /* 32px */
  --text-h2:      1.5rem;     /* 24px */
  --text-h3:      1.25rem;    /* 20px */
  --text-body-lg: 1.0625rem;  /* 17px */
  --text-body:    1rem;       /* 16px */
  --text-small:   0.875rem;   /* 14px */
  --text-caption: 0.8125rem;  /* 13px — legend tokens, dock label, prose */
  --text-micro:   0.75rem;    /* 12px */
  --text-eyebrow: 0.6875rem;  /* 11px — uppercase section eyebrows */
  --text-tiny:    0.5625rem;  /* 9px — wordmark + dev watermark only */

  /* Named off-scale sizes used in true display contexts. Keeping them
     tokenized (instead of inline `clamp()`) so future changes flow
     through index.css. */
  --text-prompt:    1.125rem;            /* 18px — agent prompt (most-recent) */
  --text-monogram:  1.625rem;            /* 26px — L mark */
  --text-hero:      clamp(56px, 14vw, 88px);
  --text-hero-sm:   clamp(40px, 10vw, 64px);  /* Hero shrunk during recording/processing */

  /* ── Spacing (4px base) ── */
  --space-2xs: 0.25rem;
  --space-xs:  0.5rem;
  --space-sm:  0.75rem;
  --space-md:  1rem;
  --space-lg:  1.5rem;
  --space-xl:  2.5rem;
  --space-2xl: 4rem;
  --space-3xl: 6rem;

  /* ── Border radius ── */
  --radius-sm:   6px;
  --radius-md:   12px;
  --radius-lg:   20px;
  --radius-xl:   28px;
  --radius-pill: 9999px;
}

@media (min-width: 1024px) {
  :root {
    --text-display: 5.5rem;   /* 88px desktop hero */
    --text-h1:      3rem;     /* 48px */
    --text-h2:      2rem;     /* 32px */
  }
}

/* ── Global keyboard focus ring ──────────────────────────────────────────
   Tailwind v4's Preflight strips default outlines on form/interactive
   elements, and our `focus:ring-*` utility classes weren't applying at
   runtime. Restore a unified, brand-consistent ring for keyboard users
   only (focus-visible) so a11y holds. Mouse focus stays clean. */
*:focus { outline: none; }
*:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 2px;
  transition: outline-color 100ms ease;
}
/* Don't double-ring already-rounded elements — keep the inset ring tight. */
button:focus-visible,
a:focus-visible,
[role="button"]:focus-visible {
  outline-offset: 3px;
}
/* Inputs/textareas: NO global focus ring. The parent pill (or per-component
   container) handles its own focus-within styling — the input itself
   stays bare so we don't stack two rings on top of each other. */
input:focus-visible,
textarea:focus-visible,
select:focus-visible {
  outline: none;
}

@layer base {
  :root {
    /* ── Motion ── */
    --ease-standard:  cubic-bezier(0.2, 0, 0, 1);
    --ease-emphasis:  cubic-bezier(0.3, 0, 0.1, 1);
    --ease-exit:      cubic-bezier(0.4, 0, 1, 1);
    --ease-breath:    cubic-bezier(0.45, 0, 0.55, 1);

    --dur-instant: 100ms;
    --dur-fast:    180ms;
    --dur-normal:  280ms;
    --dur-slow:    520ms;
    --dur-breath:  4800ms;

    /* Legacy aliases */
    --ease-out-premium: var(--ease-standard);
    --ease-in-premium:  var(--ease-exit);
    --ease-overshoot:   cubic-bezier(0.34, 1.56, 0.64, 1.00);
    --duration-fast:    var(--dur-fast);
    --duration-normal:  var(--dur-normal);
    --duration-slow:    var(--dur-slow);
  }
}

/* ── Base ── */
html, body, #root {
  height: 100%;
  margin: 0;
  padding: 0;
}

body {
  /* Page-level radial bloom: synced (via timing on .page-bloom-active class)
     with the voice button halo so the page itself breathes. Default is a
     static, low-opacity warm wash anchored beneath where the button sits. */
  background:
    radial-gradient(ellipse 60% 40% at 50% 70%, rgba(110, 152, 121, 0.08) 0%, transparent 60%),
    radial-gradient(ellipse at 50% 0%, rgba(110, 152, 121, 0.04) 0%, transparent 50%),
    var(--surface-0);
  color: var(--ink-1);
  font-family: var(--font-sans);
  font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';  /* Inter — disambiguated 1/i, soft a, etc. */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow: hidden;
}

/* ── Scrollbar ── */
::-webkit-scrollbar {
  width: 4px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(26, 22, 20, 0.1);
  border-radius: 2px;
}

/* ── Page bloom — the page itself breathes with the voice button halo.
   Apply .page-bloom to a fixed-position layer behind the voice button.
   Same 4.8s cycle as breathe-halo, opacity 6%→11%. This is the Design B
   signature move — when noticed, this is the detail that earns word of
   mouth. */
.page-bloom {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background: radial-gradient(ellipse 70% 50% at 50% 65%, rgba(110, 152, 121, 0.13) 0%, transparent 60%);
  animation: page-bloom-breathe var(--dur-breath) var(--ease-breath) infinite;
}

@keyframes page-bloom-breathe {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}

/* ── Animations ── */
@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes pulse-gentle {
  /* Softened to match the lighter idle voice button — the previous 1.03
     scale + 30px halo was contributing to the "competing CTA" feeling.
     Now an almost-imperceptible breath; the soft pulse rings around the
     button still carry the "tap me" invitation. */
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 var(--accent-glow);
  }
  50% {
    transform: scale(1.012);
    box-shadow: 0 0 20px 4px var(--accent-glow);
  }
}

@keyframes recording-pulse {
  /* Calm, confidence-instilling breathing rhythm. Per Ryan: the previous
     pulse "goes really fast" and reads as urgent. This version is much
     slower (cycle controlled by animation-duration on the consumer; we
     also lift duration there), with an almost-imperceptible scale change
     and a softer halo. The mic should feel like it's listening patiently,
     not asking for the user to hurry. */
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 var(--recording-glow);
  }
  50% {
    transform: scale(1.012);
    box-shadow: 0 0 28px 6px var(--recording-glow);
  }
}

@keyframes breathe-halo {
  /* Standalone halo for the idle voice button — invites a tap without
     demanding attention. Slow and uneven so it feels organic. */
  0%, 100% {
    box-shadow: 0 0 0 0 var(--soothe-glow);
    opacity: 0.85;
  }
  50% {
    box-shadow: 0 0 36px 14px var(--soothe-glow);
    opacity: 1;
  }
}

@keyframes token-fill-pulse {
  /* Brief celebration when a PreviewCard legend token flips from grey
     to accent green ("we caught this"). One-shot — fires once on the
     transition, no infinite loop. Subtle scale + a fading green halo. */
  0%   { transform: scale(1);     text-shadow: 0 0 0 var(--accent-glow); }
  35%  { transform: scale(1.10);  text-shadow: 0 0 12px var(--accent-glow); }
  100% { transform: scale(1);     text-shadow: 0 0 0 transparent; }
}

@keyframes inline-thinking {
  /* Opacity-only pulse for the inline ✱ in Receipt's live edge. The
     pulse-gentle keyframe was bleeding a green box-shadow halo around
     the small icon, reading as a "weird non-transparent background."
     This is just opacity + a touch of scale — no glow, no shadow. */
  0%, 100% { opacity: 0.40; transform: scale(0.94); }
  50%      { opacity: 1;    transform: scale(1.02); }
}

@keyframes thinking-asterisk {
  /* More prominent than inline-thinking — used on the InFlightArea
     processing indicator. Wider opacity range, larger scale beat,
     and a slow rotation so the asterisk reads as "actively working"
     rather than "static green glyph." Per Ryan 2026-05-06: "the
     animation for the thing needs to be a bit more prominent." */
  0%   { opacity: 0.55; transform: scale(0.90) rotate(0deg); }
  50%  { opacity: 1.00; transform: scale(1.10) rotate(60deg); }
  100% { opacity: 0.55; transform: scale(0.90) rotate(120deg); }
}

@keyframes thinking-dot {
  /* Three-dot "•  •  •" thinking indicator inside the voice button. Each
     dot uses this keyframe with a staggered delay so they pulse sequentially.
     Replaces the spinning SVG circle which provided zero progress signal. */
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
  40%           { opacity: 1;    transform: scale(1.1); }
}

@keyframes hint-fade {
  /* Soothing cross-fade for rotating prompt hints below the voice
     button. Per Ryan: "make the rotating words animation on the first
     page more soothing and assuring." Longer hold, gentler vertical
     drift (2px each way instead of 4px), and the entrance lingers
     longer (held visible for the full middle of the cycle). The
     overall effect is "the page is breathing with the message"
     rather than the message snapping in/out.
     The animation duration is 5s on .animate-hint — extending the
     hold percentages keeps the prompt visible for ~80% of that cycle. */
  0%   { opacity: 0; transform: translateY(2px); }
  20%  { opacity: 1; transform: translateY(0); }
  80%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-2px); }
}

@keyframes waveform {
  /* Calmer dance — was 0.8s on consumers (read as urgent). The bars
     still suggest "we're capturing audio" but at a confident pace
     rather than a frantic one. */
  0%, 100% { height: 10px; }
  25% { height: 20px; }
  50% { height: 14px; }
  75% { height: 26px; }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-scale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.skeleton-field {
  background: linear-gradient(
    90deg,
    var(--bg-surface-2) 25%,
    var(--bg-surface-3) 50%,
    var(--bg-surface-2) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 0.375rem;
  height: 1rem;
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out both;
}

.animate-fade-in-scale {
  animation: fade-in-scale 0.3s ease-out both;
}

.animate-slide-up {
  animation: slide-up 0.4s ease-out both;
}

.animate-pulse-gentle {
  animation: pulse-gentle 4.5s ease-in-out infinite;
}

.animate-recording-pulse {
  animation: recording-pulse 4s ease-in-out infinite;
}

.animate-breathe-halo {
  animation: breathe-halo 4s ease-in-out infinite;
}

.animate-hint {
  /* Slower cycle (5s → 6.5s) + custom easing curve that hangs at the
     top of the visibility plateau. Reads as patient — the page isn't
     trying to rush the user. Pairs with the matching slowdown of the
     rotation interval in RotatingHints.tsx (3500 → 5500ms). */
  animation: hint-fade 6.5s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

/* ── Selection ── */
::selection {
  background: var(--accent);
  color: white;
}
